package uk.co.nickthecoder.kyd.nodes

import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.collections.asMutableObservableList
import uk.co.nickthecoder.glok.event.MouseEvent
import uk.co.nickthecoder.glok.property.boilerplate.colorProperty
import uk.co.nickthecoder.glok.property.property
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.scene.Node
import uk.co.nickthecoder.glok.scene.dsl.style
import uk.co.nickthecoder.kyd.Act
import uk.co.nickthecoder.kyd.Game
import uk.co.nickthecoder.kyd.Stage
import uk.co.nickthecoder.kyd.StageDescription
import uk.co.nickthecoder.kyd.util.Vector2
import kotlin.math.round

/**
 * Contains a set of [StageView]s stacked one on top of each other.
 *
 * This base-class is used both by the editor, and when the game is being played.
 * [ActView] is used when playing the game whereas `EditActView` (in the `kyd-editor` subproject)
 * is used when editing an [Act].
 */
abstract class ActView(

    val game: Game

) : StageOrActView() {

    val actProperty by property<Act?>(null)
    var act by actProperty

    val backgroundColorProperty by colorProperty(Color.TRANSPARENT)
    var backgroundColor by backgroundColorProperty

    // TODO No. The children should NOT be mutable from the outside.
    override val children = mutableListOf<Node>().asMutableObservableList().apply {
        addChangeListener(childrenListener)
    }

    protected val stageViews = mutableListOf<StageView>()

    init {
        style("act_view")
        actProperty.addChangeListener { _, oldAct, newAct -> newAct?.let { onActChanged(oldAct, newAct) } }
    }

    fun findStageView(stageName: String): StageView? {
        return stageViews.firstOrNull { it.stage.name == stageName }
    }

    fun findStage(stageName: String): Stage? {
        return act?.stages?.firstOrNull { it.name == stageName }
    }

    /**
     * @param event An event from `Glok`; these are NOT the same as Kyd's mouse events.
     *     sceneX and sceneY are the position of the mouse in logical pixels relative to the top-left
     *     of the WINDOW (not this ActView).
     *
     * @return the position of the mouse event in the act view's world coordinates,
     *    with x & y rounded to the nearest integer.
     */
    protected fun worldPoint(event: MouseEvent): Vector2 {
        val world = Vector2(event.sceneX, event.sceneY) * sceneToWorld
        return Vector2(round(world.x), round(world.y))
    }

    protected open fun onActChanged(oldAct: Act?, act: Act) {
        children.clear()
        stageViews.clear()

        backgroundColor = act.backgroundColor
        viewPinRatio = game.viewPinRatio
        worldPin = Vector2.ZERO
        rotation = 0f
        zoom = 1f

        createStagesViews(act)
        requestLayout()
    }

    protected open fun createStagesViews(act: Act) {
        for (stageDescription in game.stageDescriptions) {
            val stage =
                act.stages.firstOrNull { it.name == stageDescription.stageName } ?: Stage(stageDescription.stageName)
            val stageView = createStageView(stage)

            stageViews.add(stageView)
            children.add(stageView)

            bindSceneProperties(stageView, stageDescription)
            bindWorldProperties(stageView, stageDescription)
        }
    }

    protected abstract fun createStageView(stage: Stage): StageView

    /**
     * Binds properties so that the [stageView] follows the values in [ActView]
     *
     * Properties bound :
     *
     * [scaleProperty], [viewPinRatioProperty]
     *
     * Unlike [bindWorldProperties], [PlayActView] and `EditActView` both do the same bindings.
     */
    protected open fun bindSceneProperties(stageView: StageView, stageDescription: StageDescription) {
        stageView.scaleProperty.bindTo(scaleProperty)
        stageView.viewPinRatioProperty.bindTo(viewPinRatioProperty)
    }

    /**
     * Bind various properties so that the [stageView] follows the values in the [ActView].
     *
     * Properties bound :
     *
     * [worldPin], [viewPinRatio], [zoomProperty], [rotationProperty].
     *
     * In addition, the four matrices
     * [worldToSceneProperty], [sceneToWorldProperty], [sceneToIntermediateProperty], [intermediateToSceneProperty]
     * are also bound (unbinding them first).
     * This prevents them being recalculated by each [StageView] - the matrices will be the same.
     *
     * NOTE. [PlayActView] overrides this method, and does nothing unless [StageDescription.linked] == `true`.
     * `EditPlayView` uses the default behaviour.
     *
     */
    protected open fun bindWorldProperties(stageView: StageView, stageDescription: StageDescription) {

        // Binding the matrices is NOT needed, but it means that each StageView doesn't recalculate them.
        // They will be the same as the [ActView]'s matrices.
        stageView.worldToSceneProperty.unbind()
        stageView.worldToSceneProperty.bindTo(worldToSceneProperty)
        stageView.sceneToWorldProperty.unbind()
        stageView.sceneToWorldProperty.bindTo(sceneToWorldProperty)
        stageView.sceneToIntermediateProperty.unbind()
        stageView.sceneToIntermediateProperty.bindTo(sceneToIntermediateProperty)
        stageView.intermediateToSceneProperty.unbind()
        stageView.intermediateToSceneProperty.bindTo(intermediateToSceneProperty)

        // These are the "base" properties that we NEED to be bound.
        stageView.worldPinProperty.bindTo(worldPinProperty)
        stageView.zoomProperty.bindTo(zoomProperty)
        stageView.rotationProperty.bindTo(rotationProperty)

    }

    // region Node methods

    override fun layoutChildren() {

        for (stageView in children) {
            setChildBounds(stageView, 0f, 0f, width, height)
        }
    }

    override fun drawChildren() {
        backend.clip(sceneX, sceneY, width, height) {
            super.drawChildren()
        }
    }

    override fun draw() {
        if (backgroundColor != Color.TRANSPARENT) {
            backend.fillRect(sceneX, sceneY, sceneX + width, sceneY + height, backgroundColor)
        }
    }

    /**
     * Draws all stages without clipping.
     * This is designed to be used in conjunction with a Frame Buffer Object (i.e. drawing to a Texture,
     * not to the screen).
     *
     * Possible uses :
     * 1. Take screenshots (save the resulting Texture to a file).
     * 2. Transitions when starting a new Act. i.e. draw the old act to an FBO, load the new act, but
     *    draw the image of the old act on top, and slide it out of way to reveal the new act.
     */
    fun drawAll() {
        draw()
        super.drawChildren()
    }

    override fun nodePrefHeight() = 0f

    override fun nodePrefWidth() = 0f

    // endregion Node methods

}
